import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiClient from '../../api/client';
import { 
  Building2, 
  MapPin,
  Calendar,
  GripVertical,
  Filter,
  RefreshCw,
  ChevronDown,
  Target,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';

// Sortable Card Component
const SortableCard = ({ card, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: `${card.core_mnemonic}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatVolume = (vol) => {
    if (!vol) return '0';
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K`;
    return vol.toString();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border rounded-lg p-3 mb-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-1 text-slate-400 hover:text-slate-600"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-slate-900 truncate text-sm">
                {card.production_nameplate || card.program}
              </p>
              {card.platform && (
                <p className="text-xs text-slate-500">{card.platform}</p>
              )}
            </div>
            <Badge variant="outline" className="text-xs shrink-0 bg-blue-50 text-blue-700 border-blue-200">
              {formatVolume(card.annual_volume)}
            </Badge>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              <span className="truncate max-w-[80px]">{card.oem}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              <span className="truncate max-w-[60px]">{card.region?.split(' ')[0]}</span>
            </div>
          </div>
          {card.assignment_source && (
            <div className="mt-2">
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  card.assignment_source === 'opportunity' 
                    ? 'bg-green-50 text-green-700 border-green-200' 
                    : card.assignment_source === 'override'
                    ? 'bg-purple-50 text-purple-700 border-purple-200'
                    : 'bg-slate-50 text-slate-600 border-slate-200'
                }`}
              >
                {card.assignment_source === 'opportunity' ? 'Won' : 
                 card.assignment_source === 'override' ? 'Manual' : 
                 card.assignment_source === 'segment_rule' ? 'Rule' : 'Unknown'}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Column Component
const KanbanColumn = ({ column, children }) => {
  const formatVolume = (vol) => {
    if (!vol) return '0';
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K`;
    return vol.toString();
  };

  const getColumnColor = () => {
    if (column.column_type === 'our_business') return 'border-t-blue-500 bg-blue-50/30';
    if (column.column_type === 'available') return 'border-t-slate-400 bg-slate-50/50';
    return 'border-t-amber-500 bg-amber-50/30';
  };

  return (
    <div className={`flex-shrink-0 w-72 rounded-lg border border-t-4 ${getColumnColor()}`}>
      <div className="p-3 border-b bg-white/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {column.column_type === 'our_business' && <Target className="h-4 w-4 text-blue-600" />}
            {column.column_type === 'available' && <AlertCircle className="h-4 w-4 text-slate-500" />}
            {column.column_type === 'competitor' && <Building2 className="h-4 w-4 text-amber-600" />}
            <h3 className="font-semibold text-slate-900 text-sm">{column.column_name}</h3>
          </div>
          <Badge variant="secondary" className="text-xs">
            {column.card_count}
          </Badge>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {formatVolume(column.total_volume)} units
        </div>
      </div>
      <ScrollArea className="h-[500px]">
        <div className="p-2">
          {children}
        </div>
      </ScrollArea>
    </div>
  );
};

// Overlay Card (shown while dragging)
const DragOverlayCard = ({ card }) => {
  const formatVolume = (vol) => {
    if (!vol) return '0';
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K`;
    return vol.toString();
  };

  return (
    <div className="bg-white border-2 border-blue-400 rounded-lg p-3 shadow-lg w-64">
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 text-blue-400 mt-1" />
        <div className="flex-1">
          <p className="font-medium text-slate-900 text-sm">
            {card.production_nameplate || card.program}
          </p>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
            <span>{card.oem}</span>
            <Badge variant="outline" className="text-xs">
              {formatVolume(card.annual_volume)}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompetitorKanban = () => {
  const queryClient = useQueryClient();
  const [selectedOem, setSelectedOem] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [minVolume, setMinVolume] = useState('10000');
  const [activeCard, setActiveCard] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Fetch available OEMs
  const { data: oemsData } = useQuery({
    queryKey: ['availableOems'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-oems');
      return response.data;
    }
  });

  // Fetch available regions
  const { data: regionsData } = useQuery({
    queryKey: ['availableRegions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-regions');
      return response.data;
    }
  });

  // Fetch Kanban board data
  const { data: boardData, isLoading, refetch } = useQuery({
    queryKey: ['kanbanBoard', selectedOem, selectedRegion, minVolume],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedOem && selectedOem !== 'all') params.append('oem', selectedOem);
      if (selectedRegion && selectedRegion !== 'all') params.append('region', selectedRegion);
      params.append('min_volume', minVolume);
      params.append('year', '2025');
      params.append('limit', '50');
      const response = await apiClient.get(`/api/competitive-intelligence/kanban/board?${params}`);
      return response.data;
    }
  });

  // Move card mutation
  const moveCardMutation = useMutation({
    mutationFn: async ({ coreMnemonic, targetCompetitorId }) => {
      const response = await apiClient.post('/api/competitive-intelligence/kanban/move-card', {
        core_mnemonic: coreMnemonic,
        target_competitor_id: targetCompetitorId
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['kanbanBoard']);
    }
  });

  const oems = oemsData?.oems || [];
  const regions = regionsData?.regions || [];
  const columns = boardData?.columns || [];

  const handleDragStart = (event) => {
    const { active } = event;
    const cardId = parseInt(active.id);
    
    // Find the card in all columns
    for (const column of columns) {
      const card = column.cards.find(c => c.core_mnemonic === cardId);
      if (card) {
        setActiveCard(card);
        break;
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over) return;

    const cardId = parseInt(active.id);
    
    // Determine target column from the over id
    let targetColumnId = null;
    
    // Check if dropped on a column header or a card
    const overId = over.id.toString();
    
    // Find which column the over item belongs to
    for (const column of columns) {
      if (column.column_id === overId) {
        targetColumnId = column.column_id;
        break;
      }
      const cardInColumn = column.cards.find(c => c.core_mnemonic.toString() === overId);
      if (cardInColumn) {
        targetColumnId = column.column_id;
        break;
      }
    }

    if (!targetColumnId) return;

    // Find the source card
    let sourceCard = null;
    let sourceColumnId = null;
    for (const column of columns) {
      const card = column.cards.find(c => c.core_mnemonic === cardId);
      if (card) {
        sourceCard = card;
        sourceColumnId = column.column_id;
        break;
      }
    }

    // Don't move if same column
    if (sourceColumnId === targetColumnId) return;

    // Don't allow moving from "Our Business" (won opportunities)
    if (sourceColumnId === 'our_business') {
      return;
    }

    // Determine the competitor ID for the target
    let targetCompetitorId = null;
    if (targetColumnId !== 'our_business' && targetColumnId !== 'available') {
      targetCompetitorId = targetColumnId;
    }

    // Execute the move
    moveCardMutation.mutate({
      coreMnemonic: cardId,
      targetCompetitorId
    });
  };

  const formatVolume = (vol) => {
    if (!vol) return '0';
    if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K`;
    return vol.toString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Competitor Board</h2>
          <p className="text-sm text-slate-500 mt-1">
            Drag and drop programs between competitors to assign market ownership
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
          </div>
          
          <Select value={selectedOem} onValueChange={setSelectedOem}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All OEMs" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All OEMs</SelectItem>
              {oems.slice(0, 50).map(oem => (
                <SelectItem key={oem} value={oem}>{oem}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions.map(region => (
                <SelectItem key={region} value={region}>{region}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500">Min:</span>
            <Input
              type="number"
              value={minVolume}
              onChange={(e) => setMinVolume(e.target.value)}
              className="w-24 h-9"
            />
          </div>
          
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Total Programs:</span>
          <span className="font-semibold">{boardData?.total_cards || 0}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Total Volume:</span>
          <span className="font-semibold">{formatVolume(boardData?.total_volume || 0)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Columns:</span>
          <span className="font-semibold">{columns.length}</span>
        </div>
      </div>

      {/* Kanban Board */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading board...</div>
      ) : columns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No data available</h3>
            <p className="text-slate-500">
              Adjust your filters or add competitors and segment rules first.
            </p>
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {columns.map((column) => (
              <SortableContext
                key={column.column_id}
                items={column.cards.map(c => `${c.core_mnemonic}`)}
                strategy={verticalListSortingStrategy}
              >
                <KanbanColumn column={column}>
                  {column.cards.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      No programs
                    </div>
                  ) : (
                    column.cards.map((card) => (
                      <SortableCard 
                        key={card.core_mnemonic} 
                        card={card}
                        isDragging={activeCard?.core_mnemonic === card.core_mnemonic}
                      />
                    ))
                  )}
                </KanbanColumn>
              </SortableContext>
            ))}
          </div>

          <DragOverlay>
            {activeCard ? <DragOverlayCard card={activeCard} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Legend */}
      <div className="flex items-center gap-6 text-xs text-slate-500 pt-4 border-t">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
          <span>Won (from Opportunities)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-purple-100 border border-purple-300" />
          <span>Manual Assignment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-slate-100 border border-slate-300" />
          <span>From Segment Rule</span>
        </div>
      </div>
    </div>
  );
};

export default CompetitorKanban;
